import { FC, ReactNode, useState } from 'react';
import { Image, Modal } from 'antd';
import { Viewer } from '@react-pdf-viewer/core';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import { getFileExtensionFromSignedUrl } from '@/utils/file-utils';

export type DocumentType = 'pdf' | 'jpg' | 'jpeg' | 'png';

export type PreviewModalState = FleetFile | null;

export const usePreviewModal = () => useState<PreviewModalState>(null);

export type PreviewModalProps = {
	file: PreviewModalState;
	onClose: () => void;
	action?: ReactNode;
};

export const renderDocument = (file: FleetFile) => {
	let type = getDocumentType(file.name);
	const url = file.url ?? null;
	if (!url) return null;

	// If name doesn't have extension, try to extract from URL
	if (!type && url) {
		const extension = getFileExtensionFromSignedUrl(url);
		if (extension) {
			// Update the name with extension for proper type detection
			file.name = file.name + extension;
			type = getDocumentType(file.name);
		}
	}

	switch (type) {
		case 'pdf':
			return <Viewer fileUrl={url} />;
		case 'jpeg':
			return (
				<Image
					style={{
						width: '100%',
						margin: '0 auto',
						height: 'auto',
					}}
					preview={false}
					src={url}
				/>
			);
		case 'jpg':
			return (
				<Image
					style={{
						width: '100%',
						margin: '0 auto',
						height: 'auto',
					}}
					preview={false}
					src={url}
				/>
			);
		case 'png':
			return (
				<Image
					style={{
						width: '100%',
						margin: '0 auto',
						height: 'auto',
					}}
					preview={false}
					src={url}
				/>
			);
		default:
			return (
				<p>
					<a
						href={url}
						target="_blank"
					>
						Open file
					</a>
				</p>
			);
	}
};

export const getDocumentType = (name: string) => {
	if (name.endsWith('.pdf')) return 'pdf';
	if (name.endsWith('.jpeg')) return 'jpeg';
	if (name.endsWith('.jpg')) return 'jpg';
	if (name.endsWith('.png')) return 'png';
};

const PreviewModal: FC<PreviewModalProps> = ({ file, onClose, action }) => {
	if (!file) return null;

	return (
		<Modal
			okText={null}
			cancelText="cancel"
			open={Boolean(file)}
			width={1440}
			closable={true}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
			footer={null}
			className="image-modal"
			styles={{
				content: {
					padding: 10,
					border: 0,
					textAlign: 'center',
				},
			}}
		>
			{renderDocument(file)}
			{action}
		</Modal>
	);
};

export default PreviewModal;
